/**
 * Web port of mobile's `MarkReadService` (see
 * `mobile/lib/features/notification/presentation/state/mark_read.service.dart`).
 *
 * Collects "subject was seen" receipts (post / comment / reply opens and
 * impressions) and flushes them to the server in **one batch** rather than a
 * call per receipt. The server resolves which notifications concern each
 * subject and returns their ids; this service hands those back to its
 * `onCleared` callback so the unread count + loaded list reconcile. The client
 * never maps subject → notification itself.
 *
 * Framework-agnostic on purpose: it owns a buffer, a periodic timer, dedup, a
 * session-suppression set, and a bounded retry — no React, no query cache, no
 * `httpClient`. The provider injects `flush` (the network call) and `onCleared`
 * (the cache reconcile) so this stays unit-testable with a fake clock and a
 * fake endpoint.
 */

/** The three subject kinds the `mark-read-by-entity` endpoint whitelists. */
export type ReceiptEntityType = 'post' | 'comment' | 'reply'

export interface ReadReceipt {
  id: string
  entityType: ReceiptEntityType
}

export interface ReadReceiptBufferOptions {
  /**
   * Sends a batch of receipts. Resolves to the notification ids the server
   * marked read (possibly empty), or `null` to signal a failure that should
   * requeue the batch. Must not throw for the requeue path — but a thrown
   * error is also treated as a failure.
   */
  flush: (receipts: ReadReceipt[]) => Promise<string[] | null>
  /** Called with the server-returned cleared ids after a successful flush. */
  onCleared: (clearedIds: string[]) => void
  /** Periodic flush cadence. Mobile uses 10s. */
  flushIntervalMs?: number
  /** Flush early once the buffer reaches this many receipts. Mobile uses 15. */
  sizeThreshold?: number
  /** Drop a receipt after this many failed flushes so a poison id can't wedge
   *  the loop. Mobile uses 3. */
  maxAttempts?: number
}

const DEFAULT_FLUSH_INTERVAL_MS = 10_000
const DEFAULT_SIZE_THRESHOLD = 15
const DEFAULT_MAX_ATTEMPTS = 3

/** Stable dedup key: a subject is identified by its kind + id. */
function keyOf(entityType: ReceiptEntityType, id: string): string {
  return `${entityType}:${id}`
}

export class ReadReceiptBuffer {
  private readonly opts: Required<ReadReceiptBufferOptions>
  /** Deduped receipts awaiting the next flush, keyed by `keyOf`. */
  private readonly pending = new Map<string, ReadReceipt>()
  /** Receipts already flushed successfully this session — suppresses
   *  re-sending when the user scrolls back over seen content. */
  private readonly sentThisSession = new Set<string>()
  /** Failed-flush counters, keyed by `keyOf`. */
  private readonly attempts = new Map<string, number>()
  private timer: ReturnType<typeof setInterval> | null = null
  private flushing = false

  constructor(options: ReadReceiptBufferOptions) {
    this.opts = {
      flushIntervalMs: DEFAULT_FLUSH_INTERVAL_MS,
      sizeThreshold: DEFAULT_SIZE_THRESHOLD,
      maxAttempts: DEFAULT_MAX_ATTEMPTS,
      ...options,
    }
    this.timer = setInterval(() => {
      void this.flush()
    }, this.opts.flushIntervalMs)
  }

  /**
   * Queues a subject to be marked read. No-ops for empty ids and for receipts
   * already sent this session. Flushes early once the buffer hits the size
   * threshold so a heavy scroll doesn't accumulate an unbounded list.
   */
  enqueue(id: string, entityType: ReceiptEntityType): void {
    if (!id) return
    const key = keyOf(entityType, id)
    if (this.sentThisSession.has(key)) return
    if (this.pending.has(key)) return
    this.pending.set(key, { id, entityType })
    if (this.pending.size >= this.opts.sizeThreshold) {
      void this.flush()
    }
  }

  /**
   * Sends the buffered receipts as one batch. On success the batch is marked
   * sent and the returned ids are handed to `onCleared`; on failure (null
   * response or thrown error) the batch is requeued up to `maxAttempts`.
   * Re-entrancy guarded so overlapping triggers (timer + threshold +
   * visibilitychange) don't double-send.
   */
  async flush(): Promise<void> {
    if (this.flushing || this.pending.size === 0) return
    this.flushing = true

    const batch = [...this.pending.entries()]
    this.pending.clear()

    try {
      const clearedIds = await this.opts.flush(batch.map(([, r]) => r))
      if (clearedIds === null) {
        this.requeue(batch)
        return
      }
      for (const [key] of batch) {
        this.sentThisSession.add(key)
        this.attempts.delete(key)
      }
      if (clearedIds.length > 0) {
        this.opts.onCleared(clearedIds)
      }
    } catch {
      this.requeue(batch)
    } finally {
      this.flushing = false
    }
  }

  private requeue(batch: Array<[string, ReadReceipt]>): void {
    for (const [key, receipt] of batch) {
      const next = (this.attempts.get(key) ?? 0) + 1
      if (next >= this.opts.maxAttempts) {
        // Give up on a persistently-failing receipt.
        this.attempts.delete(key)
      } else {
        this.attempts.set(key, next)
        // A fresh enqueue during the in-flight flush may have re-added it.
        if (!this.pending.has(key)) this.pending.set(key, receipt)
      }
    }
  }

  /** Clears all session state. Call on logout so a new user doesn't inherit
   *  the previous user's suppression set. */
  reset(): void {
    this.pending.clear()
    this.sentThisSession.clear()
    this.attempts.clear()
  }

  /** Stops the timer. Call on teardown. */
  dispose(): void {
    if (this.timer !== null) {
      clearInterval(this.timer)
      this.timer = null
    }
  }
}
